/**
 * API client utility with fetch wrapper and error handling
 *
 * Routes:
 * - Module 1 (Master Data): /materials, /customers, /suppliers, etc.
 * - Module 2 (Inventory): /api/inventory/inbound, /api/inventory/outbound, etc.
 *
 * Development: Vite proxy forwards all backend routes to http://localhost:3000
 * Production: nginx/API gateway must forward backend routes appropriately
 *
 * Usage:
 * - Module 1: api.get('/materials')
 * - Module 2: api.get('/api/inventory/inbound')
 */

import { ErrorCode, isKnownErrorCode } from '../i18n/errorCodes';
import { getStoredLocale } from '../i18n/storage';

// Production: the SPA is served same-origin by nginx, which reverse-proxies
// /api and /health to the backend. An empty base URL yields relative paths
// (/api/v1/...) that stay on-origin — no CORS, cookies carry naturally.
// Dev: the Vite dev server proxies /api to the local backend on :3000.
// Do NOT change this back to a plain `|| 'http://localhost:3000'` fallback —
// that hardcodes localhost into the production bundle and breaks deployed
// clients with "Failed to fetch" (browser hits the visitor's own machine).
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || (import.meta.env.PROD ? '' : 'http://localhost:3000');

/**
 * Get auth token from localStorage
 */
function getAuthToken(): string | null {
  try {
    const authData = localStorage.getItem('auth-storage');
    if (authData) {
      const parsed = JSON.parse(authData);
      return parsed?.state?.token || null;
    }
  } catch (error) {
    // Log auth token retrieval failures for observability
    console.warn('[API] Failed to retrieve auth token from localStorage:', error instanceof Error ? error.message : error);
  }
  return null;
}

/**
 * Update auth token in localStorage (for sliding session)
 */
function updateAuthToken(newToken: string): void {
  try {
    const authData = localStorage.getItem('auth-storage');
    if (authData) {
      const parsed = JSON.parse(authData);
      if (parsed?.state) {
        parsed.state.token = newToken;
        localStorage.setItem('auth-storage', JSON.stringify(parsed));
      }
    }
  } catch (error) {
    // Log token update failures - sliding session may not work
    console.warn('[API] Failed to update auth token in localStorage:', error instanceof Error ? error.message : error);
  }
}

/**
 * Clear auth token from localStorage (for logout on refresh failure)
 */
function clearAuthToken(): void {
  try {
    const authData = localStorage.getItem('auth-storage');
    if (authData) {
      const parsed = JSON.parse(authData);
      if (parsed?.state) {
        parsed.state.token = null;
        parsed.state.isAuthenticated = false;
        localStorage.setItem('auth-storage', JSON.stringify(parsed));
      }
    }
  } catch (error) {
    console.warn('[API] Failed to clear auth token in localStorage:', error instanceof Error ? error.message : error);
  }
}

/**
 * Token refresh state management
 */
let isRefreshingToken = false;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Backend error codes that indicate refresh definitively failed
 * Only these should trigger auth state clearing
 */
const REFRESH_FAILURE_CODES = new Set([
  'INVALID_REFRESH_TOKEN',
  'TOKEN_EXPIRED',
  'TOKEN_INVALID',
  'SESSION_EXPIRED',
]);

/**
 * Check if the refresh response indicates a definitive auth failure
 * that requires clearing persisted auth state
 */
function isDefinitiveRefreshFailure(responseData: unknown): boolean {
  if (!responseData || typeof responseData !== 'object') {
    return false;
  }
  const data = responseData as Record<string, unknown>;

  // Check for nested error.code format (backend standard)
  if (data.error && typeof data.error === 'object') {
    const error = data.error as Record<string, unknown>;
    if (typeof error.code === 'string' && REFRESH_FAILURE_CODES.has(error.code)) {
      return true;
    }
  }

  // Check for legacy flat errorCode format
  if (typeof data.errorCode === 'string' && REFRESH_FAILURE_CODES.has(data.errorCode)) {
    return true;
  }

  return false;
}

/**
 * Attempt to refresh the access token
 * Returns true if refresh was successful, false otherwise
 *
 * IMPORTANT: Only clears auth state on definitive refresh failures (INVALID_REFRESH_TOKEN),
 * not on network errors or server errors - these are transient and shouldn't cause logout
 */
async function refreshAccessToken(): Promise<boolean> {
  // If already refreshing, wait for the existing refresh to complete
  if (isRefreshingToken && refreshPromise) {
    return refreshPromise;
  }

  isRefreshingToken = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': getStoredLocale(),
        },
        credentials: 'include',
      });

      if (!response.ok) {
        // Parse response to check for definitive refresh failure
        try {
          const errorData = await response.json() as unknown;
          if (isDefinitiveRefreshFailure(errorData)) {
            // Backend explicitly indicates refresh token is invalid - clear auth
            clearAuthToken();
          }
          // For other errors (500, rate limit, etc.), don't clear auth - may be transient
        } catch {
          // Response body parse failed - don't clear auth, may be transient
        }
        return false;
      }

      const data = await response.json() as { success?: boolean; data?: { accessToken: string } };
      if (data.success && data.data?.accessToken) {
        updateAuthToken(data.data.accessToken);
        return true;
      }

      // Response OK but unexpected format - treat as failure but don't clear auth
      return false;
    } catch (error) {
      // Network error or other unexpected error - don't clear auth, may be transient
      console.warn('[API] Token refresh failed:', error instanceof Error ? error.message : error);
      return false;
    } finally {
      isRefreshingToken = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}


/**
 * Custom API error class with error code support
 */
export class ApiError extends Error {
  public readonly errorCode: ErrorCode;
  public readonly statusCode: number;
  public readonly details?: Array<{ code: string; field: string; message?: string }>;

  constructor(
    message: string,
    errorCode: ErrorCode = ErrorCode.INTERNAL_ERROR,
    statusCode: number = 500,
    details?: Array<{ code: string; field: string; message?: string }>
  ) {
    super(message);
    this.name = 'ApiError';
    this.errorCode = errorCode;
    this.statusCode = statusCode;
    this.details = details;
  }
}

/**
 * Shared request setup: builds headers with auth token, executes fetch,
 * handles sliding session, and parses error responses.
 * Returns the raw Response for callers that need custom body handling.
 */
async function fetchRaw(
  endpoint: string,
  options?: RequestInit,
  skipRetry = false,
): Promise<Response> {
  const url = endpoint;

  const token = getAuthToken();

  const headers: HeadersInit = {
    ...(options?.headers || {}),
  };

  // Only set Content-Type for requests with a body
  if (options?.body) {
    (headers as Record<string, string>)['Content-Type'] = 'application/json';
  }

  // Add Authorization header if token exists
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  // Add Accept-Language header for i18n
  const locale = getStoredLocale();
  (headers as Record<string, string>)['Accept-Language'] = locale;

  let response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  // Handle 401 Unauthorized - try to refresh token and retry once
  if (response.status === 401 && !skipRetry && token) {
    const refreshSuccess = await refreshAccessToken();
    if (refreshSuccess) {
      // Retry the request with the new token
      const newToken = getAuthToken();
      if (newToken) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, {
          ...options,
          headers,
          credentials: 'include',
        });
      }
    }
  }

  // Check for new token in response headers (sliding session)
  const newToken = response.headers.get('X-New-Token');
  if (newToken) {
    updateAuthToken(newToken);
  }

  if (!response.ok) {
    let errorMessage = 'API request failed';
    let errorCode: ErrorCode = ErrorCode.INTERNAL_ERROR;
    let errorDetails: Array<{ code: string; field: string; message?: string }> | undefined;

    try {
      const errorData = await response.json() as unknown;
      // Type guard to check if error has expected structure
      // FIX: Handle both flat errorCode (legacy) and nested error.code (new standard)
      if (errorData && typeof errorData === 'object') {
        // Check for new nested error.code format (backend core/responses/response.ts standard)
        if (
          'error' in errorData &&
          typeof errorData.error === 'object' &&
          errorData.error !== null
        ) {
          const errorObj = errorData.error as Record<string, unknown>;
          // Extract error.code from nested format
          if ('code' in errorObj && typeof errorObj.code === 'string') {
            if (isKnownErrorCode(errorObj.code)) {
              errorCode = errorObj.code as ErrorCode;
            }
          }
          // Extract message from nested format
          if ('message' in errorObj && typeof errorObj.message === 'string') {
            errorMessage = errorObj.message;
          }
        }
        // Check for legacy flat errorCode format (responseWrapper.ts)
        else if ('errorCode' in errorData && typeof errorData.errorCode === 'string') {
          if (isKnownErrorCode(errorData.errorCode)) {
            errorCode = errorData.errorCode as ErrorCode;
          }
        }

        // Extract error details if present (both formats)
        if ('details' in errorData && Array.isArray(errorData.details)) {
          errorDetails = errorData.details;
        } else if (
          'error' in errorData &&
          typeof errorData.error === 'object' &&
          errorData.error !== null &&
          'details' in errorData.error &&
          Array.isArray(errorData.error.details)
        ) {
          errorDetails = errorData.error.details as Array<{ code: string; field: string; message?: string }>;
        }

        // Fallback message extraction
        if (errorMessage === 'API request failed') {
          // Check for flat error format (string)
          if ('error' in errorData && typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          }
          // Check for flat message format
          else if ('message' in errorData && typeof errorData.message === 'string') {
            errorMessage = errorData.message;
          }
        }
      }
    } catch {
      // If JSON parsing fails, use status text
      errorMessage = response.statusText || `HTTP ${response.status}`;
    }

    // Map HTTP status codes to error codes if not provided
    if (errorCode === ErrorCode.INTERNAL_ERROR) {
      if (response.status === 401) {
        errorCode = ErrorCode.UNAUTHORIZED;
      } else if (response.status === 403) {
        errorCode = ErrorCode.PERMISSION_DENIED;
      } else if (response.status === 404) {
        errorCode = ErrorCode.NOT_FOUND;
      } else if (response.status === 400) {
        errorCode = ErrorCode.VALIDATION_ERROR;
      }
    }

    throw new ApiError(errorMessage, errorCode, response.status, errorDetails);
  }

  return response;
}

/**
 * Fetch wrapper with automatic error handling (returns parsed JSON)
 */
async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetchRaw(endpoint, options);

  // Parse JSON response with error handling
  try {
    const text = await response.text();
    if (response.status === 204 || text.trim() === '') {
      return undefined as T;
    }
    // Check if response is HTML (unexpected)
    if (text.trim().startsWith('<!doctype') || text.trim().startsWith('<html')) {
      throw new ApiError(
        'Server returned HTML instead of JSON. Check API proxy configuration.',
        ErrorCode.INTERNAL_ERROR,
        response.status
      );
    }
    return JSON.parse(text) as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      'Failed to parse API response',
      ErrorCode.INTERNAL_ERROR,
      response.status
    );
  }
}

/**
 * Fetch wrapper that returns raw text (for non-JSON responses like CSV/XML exports)
 */
async function fetchText(
  endpoint: string,
  options?: RequestInit,
): Promise<string> {
  const response = await fetchRaw(endpoint, options);
  return response.text();
}

/**
 * GET request
 */
async function get<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
  // Filter out undefined and null values, convert all to strings
  const filteredParams = params
    ? Object.fromEntries(
        Object.entries(params)
          .filter(([, value]) => value !== undefined && value !== null)
          .map(([key, value]) => [key, String(value)])
      )
    : {};

  const queryParams = Object.keys(filteredParams).length > 0
    ? `?${new URLSearchParams(filteredParams as Record<string, string>)}`
    : '';

  return fetchApi<T>(`${endpoint}${queryParams}`, {
    method: 'GET',
  });
}

/**
 * POST request
 */
async function post<T>(endpoint: string, data: unknown): Promise<T> {
  return fetchApi<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * PUT request
 */
async function put<T>(endpoint: string, data: unknown): Promise<T> {
  return fetchApi<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * DELETE request
 */
async function del<T>(endpoint: string): Promise<T> {
  return fetchApi<T>(endpoint, {
    method: 'DELETE',
  });
}

/**
 * PATCH request
 */
async function patch<T>(endpoint: string, data: unknown): Promise<T> {
  return fetchApi<T>(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * GET request that returns raw text (for non-JSON responses like exports)
 */
async function getText(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<string> {
  const filteredParams = params
    ? Object.fromEntries(
        Object.entries(params)
          .filter(([, value]) => value !== undefined && value !== null)
          .map(([key, value]) => [key, String(value)])
      )
    : {};

  const queryParams = Object.keys(filteredParams).length > 0
    ? `?${new URLSearchParams(filteredParams as Record<string, string>)}`
    : '';

  return fetchText(`${endpoint}${queryParams}`, {
    method: 'GET',
  });
}

/**
 * GET request that returns a Blob (for file downloads like exports)
 * Handles authentication and query parameters consistently with other API methods
 */
async function getBlob(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<Blob> {
  const filteredParams = params
    ? Object.fromEntries(
        Object.entries(params)
          .filter(([, value]) => value !== undefined && value !== null)
          .map(([key, value]) => [key, String(value)])
      )
    : {};

  const queryParams = Object.keys(filteredParams).length > 0
    ? `?${new URLSearchParams(filteredParams as Record<string, string>)}`
    : '';

  const response = await fetchRaw(`${endpoint}${queryParams}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/octet-stream',
    },
  });

  return response.blob();
}

/**
 * POST request with FormData (for file uploads)
 * Does NOT set Content-Type header - browser will set it with boundary
 */
async function postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
  const token = getAuthToken();
  const locale = getStoredLocale();

  const headers: HeadersInit = {
    'Accept-Language': locale,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  });

  // Handle 401 Unauthorized - try to refresh token and retry once
  if (response.status === 401 && token) {
    const refreshSuccess = await refreshAccessToken();
    if (refreshSuccess) {
      const newToken = getAuthToken();
      if (newToken) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: formData,
          credentials: 'include',
        });
        if (retryResponse.ok) {
          return retryResponse.json() as Promise<T>;
        }
      }
    }
  }

  if (!response.ok) {
    let errorMessage = 'Upload failed';
    let errorCode: ErrorCode = ErrorCode.INTERNAL_ERROR;

    try {
      const errorData = await response.json() as unknown;
      if (errorData && typeof errorData === 'object') {
        if ('error' in errorData && typeof errorData.error === 'object' && errorData.error !== null) {
          const errorObj = errorData.error as Record<string, unknown>;
          if ('message' in errorObj && typeof errorObj.message === 'string') {
            errorMessage = errorObj.message;
          }
          if ('code' in errorObj && typeof errorObj.code === 'string' && isKnownErrorCode(errorObj.code)) {
            errorCode = errorObj.code as ErrorCode;
          }
        } else if ('message' in errorData && typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
      }
    } catch {
      errorMessage = response.statusText || `HTTP ${response.status}`;
    }

    throw new ApiError(errorMessage, errorCode, response.status);
  }

  return response.json() as Promise<T>;
}

export const api = {
  get,
  getText,
  getBlob,
  post,
  postFormData,
  put,
  patch,
  delete: del,
};
